import type { Permission } from '@idc-forms/permissions'
import {
  Briefcase,
  Building2,
  ChevronRight,
  CopyCheck,
  CreditCard,
  Handshake,
  LayoutDashboard,
  type LucideIcon,
  Smartphone,
  UserRound,
  Workflow
} from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation } from 'react-router'
import { preloadAdminRoute } from '@/app/router/route-preloaders'
import { SEGMENTS } from '@/app/router/routes'
import { FEATURES } from '@/shared/config/features'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/shared/ui/sidebar'
import MenuBusiness, { useHasBusinessItems } from './menu-business'

interface NavItem {
  label: string
  icon: LucideIcon
  to: string
  segment: string
  permission?: Permission | Permission[]
}

/**
 * Orden pedido por el cliente (transcripción l.13-31 / SPEC-11):
 * Inicio · Clientes · Inventario · Negociaciones · Créditos · Postventa
 *
 * ☠️ `isAdmin` saltea el filtro de permisos más abajo: el ítem del menú y su ruta
 * registrada viajan JUNTOS o un admin ve un link a una pantalla en blanco
 * (misma trampa documentada para Flujos y Cuentas de la app).
 */
const NAV_ITEMS: NavItem[] = [
  { label: 'Inicio', icon: LayoutDashboard, to: SEGMENTS.admin.dashboard, segment: 'dashboard' },
  {
    label: 'Clientes',
    icon: UserRound,
    to: SEGMENTS.admin.clients,
    segment: 'clients',
    permission: ['CreateClient', 'ReadAllClients', 'UpdateClient'] as Permission[]
  },
  {
    label: 'Inventario',
    icon: Briefcase,
    to: SEGMENTS.admin.inventory,
    segment: 'inventory',
    permission: 'ReadAvailability' as Permission
  },
  {
    label: 'Negociaciones',
    icon: Handshake,
    to: SEGMENTS.admin.negotiations,
    segment: 'negotiations',
    permission: ['CreateNegotiation', 'ReadAllNegotiations', 'UpdateNegotiation'] as Permission[]
  },
  {
    label: 'Créditos',
    icon: CreditCard,
    to: SEGMENTS.admin.creditos,
    segment: 'creditos',
    // Los DOS permisos de consulta, igual que la puerta de la API: el base lo tiene el asesor que
    // ve su cartera, el ancho quien ve todos los expedientes, y el alcance lo decide el servidor.
    // Con sólo uno de los dos, alguno de esos dos perfiles no vería el ítem de una pantalla que sí
    // puede abrir. Son los mismos que gatean la ruta (`credits.routes.tsx`) — viajan juntos.
    permission: ['ReadCredits', 'ReadAllCredits'] as Permission[]
  },
  {
    label: 'Postventa',
    icon: CopyCheck,
    to: SEGMENTS.admin.deliveries,
    segment: 'deliveries',
    permission: 'ReadAllDeliveries' as Permission
  },
  // Siempre en el menú: la consola de cuentas no depende de que la app esté publicada, sólo sus
  // acciones hacia el cliente (`FEATURES.clientAppLaunched`). Lo gobierna el permiso.
  {
    label: 'Cuentas de la app',
    icon: Smartphone,
    to: `${SEGMENTS.admin.clients}/app-accounts`,
    segment: 'app-accounts',
    permission: 'ReadClientAppAccounts' as Permission
  },
  // El motor de flujos ES el portal —define qué secciones llena el cliente—, así que sigue su
  // flag y no su permiso. El permiso no alcanzaba: `isAdmin` saltea el filtro por permisos más
  // abajo, así que un administrador veía el ítem igual. Y la RUTA sí está gateada en
  // `router-config`, con lo cual el ítem llevaba a una pantalla en blanco.
  ...(FEATURES.clientPortal
    ? [
        {
          label: 'Flujos',
          icon: Workflow,
          to: SEGMENTS.admin.flows,
          segment: 'flows',
          permission: 'ReadFlowDefinitions' as Permission
        }
      ]
    : [])
]

export function NavMain() {
  const pathname = useLocation().pathname
  const segments = pathname.split('/').filter(Boolean)
  const { has, hasAny, isAdmin } = usePermissions()

  const visibleItems = useMemo(() => {
    if (isAdmin) return NAV_ITEMS
    return NAV_ITEMS.filter((item) => {
      if (!item.permission) return true
      return Array.isArray(item.permission) ? hasAny(item.permission) : has(item.permission)
    })
  }, [isAdmin, has, hasAny])

  const showBusiness = useHasBusinessItems()

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Plataforma</SidebarGroupLabel>
      <SidebarMenu>
        {visibleItems.map((item) => (
          <SidebarMenuItem key={item.segment}>
            <SidebarMenuButton
              asChild
              tooltip={item.label}
              isActive={segments.includes(item.segment)}
              className="data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground"
            >
              <Link
                to={item.to}
                onFocus={() => {
                  void preloadAdminRoute(item.segment)
                }}
                onPointerEnter={() => {
                  void preloadAdminRoute(item.segment)
                }}
              >
                <item.icon />
                <span>{item.label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}

        {showBusiness && (
          <Collapsible asChild defaultOpen={true}>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={'Empresa'}
                isActive={segments.includes('business')}
                className="data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground"
              >
                <Link
                  to={SEGMENTS.admin.business}
                  onFocus={() => {
                    void preloadAdminRoute(SEGMENTS.admin.business)
                  }}
                  onPointerEnter={() => {
                    void preloadAdminRoute(SEGMENTS.admin.business)
                  }}
                >
                  <Building2 />
                  <span>Empresa</span>
                </Link>
              </SidebarMenuButton>
              <CollapsibleTrigger asChild>
                <SidebarMenuAction className="data-[state=open]:rotate-90">
                  <ChevronRight />
                  <span className="sr-only">Toggle</span>
                </SidebarMenuAction>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <MenuBusiness />
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        )}
      </SidebarMenu>
    </SidebarGroup>
  )
}
